import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, StatusBadge, Spinner, Button, money } from "@/components/ui";
import { toast } from "sonner";
import { ArrowLeft, Copy, Check, ExternalLink, RefreshCw, Mail } from "lucide-react";

export default function InvoiceDetail() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  async function load() {
    try {
      const res = await api.get(`/admin/invoices/${id}`);
      setInv(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function copyLink() {
    navigator.clipboard.writeText(inv.pay_link);
    setCopied(true);
    toast.success("Pay link copied");
    setTimeout(() => setCopied(false), 1500);
  }

  async function syncStatus() {
    try {
      const res = await api.post(`/admin/invoices/${id}/sync`);
      setInv(res.data);
      toast.success("Status refreshed from Stripe");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function sendEmail() {
    try {
      const res = await api.post(`/admin/invoices/${id}/send`);
      toast.success(`Emailed ${res.data.to}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (loading) return <div className="flex justify-center py-20 text-primary"><Spinner /></div>;
  if (!inv) return <p className="text-muted-foreground">Invoice not found.</p>;

  const unpaid = inv.status === "sent" || inv.status === "failed";

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{inv.customer_name}</h1>
          <p className="text-sm text-muted-foreground">{inv.customer_email}</p>
          {inv.invoice_number && (
            <p className="text-xs text-muted-foreground mt-1">
              Invoice {inv.invoice_number}
              {inv.invoice_pdf && (
                <> · <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="text-primary hover:underline">PDF</a></>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={inv.status} />
          <Button variant="ghost" size="sm" onClick={syncStatus} title="Refresh status from Stripe"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Pay link */}
      <Card className="space-y-3">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Payment link</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded-lg bg-secondary/60 border border-border px-3 py-2.5 text-sm">
            {inv.pay_link}
          </code>
          <Button variant="outline" size="sm" onClick={copyLink}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
          <a href={inv.pay_link} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm"><ExternalLink className="h-4 w-4" /></Button>
          </a>
        </div>
        {unpaid && (
          <Button variant="outline" size="sm" onClick={sendEmail} className="w-full">
            <Mail className="h-4 w-4" /> Email invoice to customer
          </Button>
        )}
        {unpaid
          ? <p className="text-xs text-muted-foreground">Send this link to your client, or email it above. It opens a secure Stripe page and never expires.</p>
          : <p className="text-xs text-emerald-400">This invoice has been paid{inv.has_subscription ? " and the monthly subscription is active." : "."}</p>}
      </Card>

      {/* Line items */}
      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <tbody>
            {inv.line_items.map((li, i) => (
              <tr key={i} className="border-b border-border/50">
                <td className="px-4 py-3">{li.description}</td>
                <td className="px-4 py-3 text-right">
                  <span className="mono">{money(Math.round(li.amount * 100), inv.currency)}</span>
                  {li.type === "monthly" && <span className="text-primary text-xs">/mo</span>}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border">
              <td className="px-4 py-3 text-muted-foreground">Due now</td>
              <td className="px-4 py-3 text-right mono font-semibold">
                {money(inv.one_time_total_cents + inv.monthly_total_cents, inv.currency)}
              </td>
            </tr>
            {inv.monthly_total_cents > 0 && (
              <tr>
                <td className="px-4 py-3 text-muted-foreground">Recurring</td>
                <td className="px-4 py-3 text-right mono font-semibold text-primary">
                  {money(inv.monthly_total_cents, inv.currency)}/mo
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </Card>
    </div>
  );
}
